/**
 * firstFetchPlan — what Month/Week put into `filters` on mount, as pure
 * functions so the number of events requests a cold load costs is testable.
 *
 * The events fetch keys on the serialized server filters, so every value
 * changed AFTER the first fetch is one more request. Both values below are
 * known at mount from the SSR seed (`readOrgSeed`); using them there makes the
 * later bootstrap / org-timezone corrections no-ops.
 *
 * @module firstFetchPlan
 */

/**
 * Timezone to dispatch at mount, or `null` for "leave filters.timezone alone".
 * A restored session value or an already-set non-default value wins; otherwise
 * the org's timezone (seed) beats the browser's, which beats `'UTC'`.
 *
 * @param {Object} s
 * @param {string} [s.restoredTimezone]
 * @param {string} [s.currentTimezone]  `filters.timezone` before mount.
 * @param {string|null} [s.seedTimezone]
 * @param {string|null} [s.browserTimezone]
 * @returns {string|null}
 */
export function interimTimezone({
	restoredTimezone,
	currentTimezone,
	seedTimezone,
	browserTimezone
} = {}) {
	if (restoredTimezone) return null;
	if (currentTimezone !== 'UTC') return null;
	return seedTimezone || browserTimezone || null;
}

/**
 * The org filter to dispatch at mount, or `null` when the restored one is
 * already right. The target is the CANONICAL slug when the seed has it: the
 * URL segment is undecoded and may differ cosmetically (case, encoding) from
 * what bootstrap later returns, and the org lock would then correct it with a
 * second fetch.
 *
 * @param {Object} s
 * @param {Array|undefined} s.restoredOrg
 * @param {string} s.urlOrgUnique
 * @param {string|null} [s.seedOrgUnique]
 * @returns {string[]|null}
 */
export function orgFilterToSeed({ restoredOrg, urlOrgUnique, seedOrgUnique } = {}) {
	const target = seedOrgUnique || urlOrgUnique;
	if (!target) return null;
	const same = Array.isArray(restoredOrg) && restoredOrg.length === 1 && restoredOrg[0] === target;
	return same ? null : [target];
}

/**
 * Does the post-bootstrap org lock have anything to correct? Exact match: the
 * events are filtered client-side by `organizationUnique`, so `filters.org`
 * must hold the canonical slug itself, not an equivalent spelling.
 *
 * @param {Array|unknown} filterOrg
 * @param {string} currentOrgUnique
 * @returns {boolean}
 */
export function orgLockDrifted(filterOrg, currentOrgUnique) {
	if (!currentOrgUnique) return false;
	const arr = Array.isArray(filterOrg) ? filterOrg : [];
	return !(arr.length === 1 && arr[0] === currentOrgUnique);
}
